import React, { useEffect } from 'react';
import { Plus, Trash2, Move, ChevronDown, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load additional fonts only when TextOverlay is used (with Cyrillic subset)
const EXTRA_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700&family=Oswald:wght@400;700&family=Playfair+Display:wght@400;700&family=Lobster&family=Pacifico&family=Dancing+Script:wght@400;700&family=Raleway:wght@400;700&family=Nunito:wght@400;700&family=Ubuntu:wght@400;700&family=Rubik:wght@400;700&family=Comfortaa:wght@400;700&family=PT+Sans:wght@400;700&family=PT+Serif:wght@400;700&family=Merriweather:wght@400;700&family=Fira+Sans:wght@400;700&family=Jost:wght@400;700&family=Marck+Script&family=Caveat:wght@400;700&family=Cormorant+Garamond:wght@400;700&family=Philosopher:wght@400;700&family=Pangolin&family=Neucha&family=Ruslan+Display&family=Press+Start+2P&family=Bad+Script&family=Yeseva+One&family=Poiret+One&family=Kelly+Slab&family=Tenor+Sans&display=swap&subset=cyrillic,cyrillic-ext';
let fontsLoaded = false;
export type TextAlign = 'left' | 'center' | 'right';

export interface TextItem {
  id: string;
  text: string;
  font: string;
  size: number;
  color: string;
  x: number;
  y: number;
  shadow: boolean;
  stroke: boolean;
  strokeColor: string;
  strokeWidth: number;
  textAlign: TextAlign;
  boxWidth: number;
}

const FONTS = [
  // Sans-serif (современные)
  { id: 'Outfit', name: 'Outfit' },
  { id: 'Roboto', name: 'Roboto' },
  { id: 'Open Sans', name: 'Open Sans' },
  { id: 'Montserrat', name: 'Montserrat' },
  { id: 'Raleway', name: 'Raleway' },
  { id: 'Nunito', name: 'Nunito' },
  { id: 'Ubuntu', name: 'Ubuntu' },
  { id: 'Rubik', name: 'Rubik' },
  { id: 'Comfortaa', name: 'Comfortaa' },
  { id: 'PT Sans', name: 'PT Sans' },
  { id: 'Fira Sans', name: 'Fira Sans' },
  { id: 'Jost', name: 'Jost' },
  { id: 'Oswald', name: 'Oswald' },
  { id: 'Lato', name: 'Lato' },
  { id: 'Tenor Sans', name: 'Tenor Sans' },
  // Serif (элегантные)
  { id: 'Playfair Display', name: 'Playfair Display' },
  { id: 'PT Serif', name: 'PT Serif' },
  { id: 'Merriweather', name: 'Merriweather' },
  { id: 'Cormorant Garamond', name: 'Cormorant Garamond' },
  { id: 'Philosopher', name: 'Philosopher' },
  { id: 'Yeseva One', name: 'Yeseva One' },
  { id: 'Georgia', name: 'Georgia' },
  // Рукописные / декоративные
  { id: 'Lobster', name: 'Lobster' },
  { id: 'Pacifico', name: 'Pacifico' },
  { id: 'Dancing Script', name: 'Dancing Script' },
  { id: 'Marck Script', name: 'Marck Script' },
  { id: 'Caveat', name: 'Caveat' },
  { id: 'Pangolin', name: 'Pangolin' },
  { id: 'Neucha', name: 'Neucha' },
  { id: 'Bad Script', name: 'Bad Script' },
  // Дисплейные / креативные
  { id: 'Ruslan Display', name: 'Ruslan Display' },
  { id: 'Press Start 2P', name: 'Press Start 2P' },
  { id: 'Poiret One', name: 'Poiret One' },
  { id: 'Kelly Slab', name: 'Kelly Slab' },
  { id: 'Impact', name: 'Impact' },
  { id: 'Arial', name: 'Arial' },
];

const TEXT_COLORS = [
  '#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff',
  '#ffff00', '#ff00ff', '#00ffff', '#ff6600', '#9900ff',
];

interface TextOverlayProps {
  textItems: TextItem[];
  onAddText: () => string;
  onUpdateText: (id: string, updates: Partial<TextItem>) => void;
  onRemoveText: (id: string) => void;
}

const TextOverlay: React.FC<TextOverlayProps> = ({
  textItems,
  onAddText,
  onUpdateText,
  onRemoveText,
}) => {
  const { t } = useLanguage();
  
  // Lazy load extra fonts on first render
  useEffect(() => {
    if (!fontsLoaded) {
      fontsLoaded = true;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = EXTRA_FONTS_URL;
      document.head.appendChild(link);
    }
  }, []);
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="panel-title font-semibold text-foreground">{t.captions}</h2>
        <Button variant="ghost" size="sm" onClick={() => onAddText()} aria-label="Add text">
          <Plus className="w-4 h-4 mr-1" />
          {t.add}
        </Button>
      </div>

      {textItems.length === 0 && (
        <p className="panel-label text-muted-foreground text-center py-4">
          {t.clickAddToCreateCaption}
        </p>
      )}

      {textItems.length > 0 && (
        <div className="space-y-3">
          {textItems.map((item) => (
            <Collapsible key={item.id} defaultOpen>
              <div className="p-3 rounded-lg border border-border bg-card/50 space-y-3">
                {/* Кнопки выравнивания над полем ввода */}
                <div className="flex gap-1 justify-center">
                  <Button
                    variant={item.textAlign === 'left' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 w-9 p-0"
                    onClick={() => onUpdateText(item.id, { textAlign: 'left' })}
                    aria-label="Align left"
                  >
                    <AlignLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={item.textAlign === 'center' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 w-9 p-0"
                    onClick={() => onUpdateText(item.id, { textAlign: 'center' })}
                    aria-label="Align center"
                  >
                    <AlignCenter className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={item.textAlign === 'right' ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 w-9 p-0"
                    onClick={() => onUpdateText(item.id, { textAlign: 'right' })}
                    aria-label="Align right"
                  >
                    <AlignRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Поле ввода текста */}
                <div className="flex items-start gap-2">
                  <Textarea
                    value={item.text}
                    onChange={(e) => onUpdateText(item.id, { text: e.target.value })}
                    placeholder={t.enterText}
                    className="min-h-[60px] text-sm flex-1 resize-y"
                    style={{ textAlign: item.textAlign || 'center' }}
                    rows={2}
                  />
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 hover:bg-destructive/20"
                      onClick={() => onRemoveText(item.id)}
                      aria-label="Remove text"
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Toggle text settings">
                        <ChevronDown className="w-4 h-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                </div>

                <CollapsibleContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="panel-label text-muted-foreground">{t.font}</Label>
                      <Select
                        value={item.font}
                        onValueChange={(value) => onUpdateText(item.id, { font: value })}
                      >
                        <SelectTrigger 
                          className="h-8 text-xs" 
                          aria-label="Select font"
                          style={{ fontFamily: item.font }}
                        >
                          <SelectValue placeholder={item.font} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] overflow-y-auto">
                          {FONTS.map((font) => (
                            <SelectItem 
                              key={font.id} 
                              value={font.id} 
                              style={{ fontFamily: `"${font.id}", sans-serif` }}
                              className="text-sm py-2"
                            >
                              {font.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="panel-label text-muted-foreground">{t.size}: {item.size}px</Label>
                      <Slider
                        value={[item.size]}
                        onValueChange={([v]) => onUpdateText(item.id, { size: v })}
                        min={12}
                        max={120}
                        step={2}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="panel-label text-muted-foreground">{t.color}</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {TEXT_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => onUpdateText(item.id, { color })}
                          aria-label={`Text color ${color}`}
                          className={`w-6 h-6 rounded border-2 transition-all ${
                            item.color === color
                              ? 'border-primary scale-110'
                              : color === '#ffffff' || color === '#FFFFFF'
                                ? 'border-muted-foreground/30 hover:scale-105'
                                : 'border-transparent hover:scale-105'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="panel-label text-muted-foreground">{t.shadow}</Label>
                    <Switch
                      checked={item.shadow}
                      onCheckedChange={(checked) => onUpdateText(item.id, { shadow: checked })}
                    />
                  </div>

                  {/* Stroke / Outline settings */}
                  <div className="flex items-center justify-between">
                    <Label className="panel-label text-muted-foreground">{t.stroke}</Label>
                    <Switch
                      checked={item.stroke}
                      onCheckedChange={(checked) => onUpdateText(item.id, { stroke: checked })}
                    />
                  </div>

                  {item.stroke && (
                    <div className="space-y-3 pl-2 border-l-2 border-border">
                      <div className="space-y-2">
                        <Label className="panel-label text-muted-foreground">{t.strokeColor}</Label>
                        <div className="flex gap-1.5 flex-wrap">
                          {TEXT_COLORS.map((color) => (
                            <button
                              key={color}
                              onClick={() => onUpdateText(item.id, { strokeColor: color })}
                              aria-label={`Stroke color ${color}`}
                              className={`w-6 h-6 rounded border-2 transition-all ${
                                item.strokeColor === color
                                  ? 'border-primary scale-110'
                                  : color === '#ffffff' || color === '#FFFFFF'
                                    ? 'border-muted-foreground/30 hover:scale-105'
                                    : 'border-transparent hover:scale-105'
                              }`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="panel-label text-muted-foreground">{t.strokeWidth}: {item.strokeWidth}px</Label>
                        <Slider
                          value={[item.strokeWidth]}
                          onValueChange={([v]) => onUpdateText(item.id, { strokeWidth: v })}
                          min={1}
                          max={10}
                          step={1}
                        />
                      </div>
                    </div>
                  )}

                  <p className="panel-label text-muted-foreground flex items-center gap-1">
                    <Move className="w-3 h-3" />
                    {t.dragTextOnCanvas}
                  </p>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
};

export default TextOverlay;